<template>
  <Confirmation
    v-if="isModalVisible"
    @onDelete="deleteItem"
    :message="deleteMessage"
  ></Confirmation>
  <div class="flex flex-col">
    <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
      <div class="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
        <div
          class="overflow-hidden border-b border-gray-200 shadow sm:rounded-lg"
        >
          <table :key="version" class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                  v-for="header in headers.data"
                  :key="header.name"
                >
                  <div class="flex" @click="header.sortable ? sortBy(header.value) : ''" :class="{'cursor-pointer': header.sortable }">
                    <span
                      :class="{
                        'font-extrabold': currentSort?.column === header.value,
                      }"
                    >
                      {{ header.label }}
                    </span>
                    <ArrowSmUpIcon
                      v-if="header.sortable"
                      class="ml-4 h-4 w-4 transform transition-all duration-200 hover:scale-125"
                      :class="{
                        'rotate-180 transform':
                          currentSort?.column &&
                          currentSort?.direction === 'desc',
                      }"
                    />
                  </div>
                </th>
                <th scope="col" class="relative px-6 py-3" v-if="routes.edit">
                  <span class="sr-only">Wijzig</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, i) in items"
                :key="item.id"
                :class="i % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <slot name="row">
                  <td
                    class="whitespace-nowrap px-6 py-4 text-sm font-medium"
                    v-for="(header, _i) in headers.data"
                    :key="`${item.id}_${header.name}`"
                    :class="{ 'text-gray-900': _i === 0 }"
                  >
                    <template v-if="header.type === 'property'">
                      <span>
                        {{ getPropByString(items[i], header.value) }}
                      </span>
                    </template>
                    <span v-else-if="header.type === 'number'">
                      <Number
                        :editable="header.editable"
                        :id="getPropByString(items[i], 'id')"
                        :number="getPropByString(items[i], header.value)"
                        :update_route="routes.update"
                        :attribute_name="header.value"
                      />
                    </span>
                    <span v-else-if="header.type === 'boolean'">
                      <Checkbox
                        :editable="header.editable"
                        :id="getPropByString(items[i], 'id')"
                        :checked="getPropByString(items[i], header.value)"
                        :update_route="routes.update"
                        :attribute_name="header.value"
                      />
                    </span>
                    <span v-else-if="header.type === 'label'">
                      <Label
                        :value="getPropByString(items[i], header.value)"
                        :options="header.options"
                      />
                    </span>
                    <span v-else-if="header.type === 'rating'">
                      <Rating :header="header" :item="items[i]" />
                    </span>
                    <span v-else-if="header.type === 'trend'">
                      <Trend :trend="getPropByString(items[i], header.value)" />
                    </span>
                    <span v-else-if="header.type === 'download'">
                      <Download
                        :value="getPropByString(items[i], header.value)"
                      />
                    </span>
                    <span v-else-if="header.type === 'link'">
                      <a
                        :href="header.value"
                        v-bind:target="header.options._target"
                      >
                        {{ header.label }}
                      </a>
                    </span>
                    <span v-else-if="header.type === 'array'">
                      <span v-for="(fields, key) in header.value">
                        <span v-for="(entity, entityKey) in items[i][key]">
                          <span v-for="field in fields">
                            {{ entity[field] }}
                          </span>
                          <span
                            v-if="entityKey < items[i][key].length - 1"
                            v-html="header.options.separator"
                          >
                          </span>
                        </span>
                      </span>
                    </span>
                    <span v-else-if="header.type === 'date'">
                      <span
                        v-text="moment(item[header.value], header.options.format)"
                      ></span>
                    </span>
                  </td>
                  <td
                    v-if="routes.edit"
                    class="space-x-2 whitespace-nowrap px-2 py-4 text-right text-sm font-medium"
                  >
                    <Link
                      :href="route(routes.edit.name, [items[i][routes.edit.identifier]])"
                      class="text-indigo-600 hover:text-indigo-900"
                    >
                      Wijzig
                    </Link>
                    <span v-if="routes.destroy" class="inline-block">
                      <button
                        v-on:click="openModal(item)"
                        class="text-red-600 hover:text-red-900"
                      >
                        Verwijder
                      </button>
                    </span>
                  </td>
                </slot>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Helpers from '@admin_js/Services/Helpers'
import { Link } from '@inertiajs/inertia-vue3'
import { reactive } from 'vue'
import Confirmation from '@common/Confirmation'
import { Inertia } from '@inertiajs/inertia'
import Number from '@common/TableFields/Number.vue'
import Checkbox from '@common/TableFields/Checkbox.vue'
import Label from '@common/TableFields/Label.vue'
import Download from './TableFields/Download'
import { ArrowSmUpIcon } from '@heroicons/vue/solid'
import Trend from '@common/TableFields/Trend.vue'
import Rating from '@common/TableFields/Rating.vue'

const { DateTime } = require('luxon')

export default {
  setup() {
    const form = reactive({
      id: null,
    })

    return { form }
  },
  data() {
    return {
      isModalVisible: false,
      toDelete: null,
      version: 0,
    }
  },
  beforeUpdate() {
    this.version++
  },
  mixins: [Helpers],
  components: {
    Download,
    Link,
    Confirmation,
    Number,
    Checkbox,
    Label,
    Rating,
    ArrowSmUpIcon,
    Trend,
  },
  props: {
    headers: {},
    items: {},
    routes: {
      type: Object,
    },
    deleteMessage: {
      type: String,
      default: 'Weet je zeker dat je dit wilt verwijderen.',
    },
  },
  mounted() {},
  computed: {
    currentSort() {
      return route().params.sort
    },
  },
  methods: {
    moment(date, format) {
      return DateTime.fromISO(date).toLocaleString(format)
    },
    deleteItem(boolean) {
      this.isModalVisible = false

      if (boolean === true) {
        this.form['_method'] = 'delete'
        Inertia.post(
          route(this.routes.destroy, { id: this.toDelete }),
          this.form
        )
      }

      this.toDelete = null
    },
    openModal(item) {
      this.isModalVisible = true
      this.toDelete = item.id
    },
    sortBy(column) {
      let _params = route().params

      if (_params.sort && _params.sort.column === column) {
        _params.sort.direction =
          _params.sort.direction === 'asc' ? 'desc' : 'asc'
      } else {
        _params.sort = { column, direction: 'asc' }
      }
      Inertia.get(route(route().current()), _params)
    },
  },
}
</script>
